import {wrapError} from '@northpoint-development/labs-common-core';

import {createDocRef, runInTransaction} from '@/services/FirestoreService';
import {createUpdateQueue} from '@/services/FirestoreService/updateQueue';
import {callProcedure} from '@/services/BigQueryService';
import {getStore} from '@/services/store';
import {flushOpenReviewEdits} from '@/services/reviewEdits';
import {Markers} from '@/services/MapService';
import {getCollection} from '@/lib/filters/filters';
import {
  buildCommittedCompetitiveBody,
  buildCommittedMarkerData,
  formatOmComps,
  addAggregatedFieldsToBody,
  nextUnresolvedSibling,
  planSurvivorReleases,
  refreshBackendFiltersForDocs,
  showReleasedSurvivors,
} from '@/services/OMService';

import {currentUserAction} from '@/services/currentUserAction';

import {placeBulkMarker} from './place';
import {bulkConfig, BulkConfigItem} from './config';
import {byStagedName} from './ordering';
import type {StagedRecord, StagedUserAction} from './types';
import type {DisplayField} from '@/lib/classes/InfoWindowContent/DisplayField';
import type {OMItem} from '@/services/OMService/types';

// Explicit teardown — a single-tab review window can't rely on the info window's
// `closeActions` firing on close, so the staging marker would otherwise linger.
const removeStagingMarker = (cfg: BulkConfigItem, id: string): void => {
  if (Markers.exists(cfg.localCollection, id)) {
    Markers.delete(cfg.localCollection, id);
  }
};

/** Committed-record payload for the read-only info-window tab opened after a bundle commits. */
const committedSubject = (cfg: BulkConfigItem, body: object) => ({
  ...body,
  firestoreCollection: cfg.finalFirestoreCollection,
  filterCollection: cfg.finalFilterCollection,
});

/**
 * Copies the staging doc's changelog rows onto the committed doc's id (as OM does at approval),
 * so review-time edits show as their own Version History lines. Fire-and-forget: history
 * enrichment must never block or fail a commit.
 */
const copyStagingAuditRows = (cfg: BulkConfigItem, stagingId: string, committedId: string): void => {
  callProcedure('firestore_export', 'copyOmRows', [
    cfg.bigQuerySourceTable,
    cfg.bigQueryDestTable,
    stagingId,
    committedId,
  ]).catch((error: unknown) => console.error('Failed to copy bulk staging audit rows:', error));
};

const buildCommittedBody = (
  record: StagedRecord,
  id: string,
  linkedDocuments: {collection: string; id: string}[],
  whoAndNow: StagedUserAction,
) => {
  if (!record.location) {
    throw new Error(`Cannot commit ${record.kind} "${record.name}" (${record.id}): missing location`);
  }
  return buildCommittedCompetitiveBody({
    id,
    name: record.name,
    location: record.location,
    // Staged records store these loosely (`object[]`/`StagedComp[]`) but carry the committed shape.
    displayFields: record.displayFields as DisplayField[],
    comps: record.kind === 'building' ? record.comps as OMItem['comps'] : null,
    underwritingData: {},
    linkedDocuments,
    generatedByOm: false,
    whoAndNow,
    bulkBatchId: record.bulkBatchId,
  });
};

/**
 * The portfolio a record belongs to (itself if it IS a portfolio), or null for a standalone
 * building. Resolved from the per-user store, so a member of another user's portfolio reads as
 * standalone — advancement stays within the current user's visible bundle.
 */
function bundlePortfolio(item: StagedRecord): StagedRecord | null {
  if (item.kind === 'portfolio') return item;
  const portfolioId = item.linkedDocuments.find((d) => d.collection === bulkConfig.portfolio.stagingCollection)?.id;
  if (!portfolioId) return null;
  return (getStore().state.bulkCompetitivePortfolios as StagedRecord[]).find((p) => p.id === portfolioId) ?? null;
}

interface CommittedRecord {
  record: StagedRecord;
  cfg: BulkConfigItem;
  writeable: ReturnType<typeof buildCommittedCompetitiveBody>;
}

type BundleOutcome =
  | {kind: 'alreadyResolved'; status: StagedRecord['status']}
  | {kind: 'nextForReview'; next: StagedRecord}
  | {kind: 'resolved'; committed: CommittedRecord[]; releases: Awaited<ReturnType<typeof planSurvivorReleases>>};

/** Resolves whether it opened a committed record. */
const showCommittedRecords = (committed: CommittedRecord[]): boolean => {
  let portfolioBody = null;
  const buildingBodies = [];
  for (const {record, cfg, writeable} of committed) {
    copyStagingAuditRows(cfg, record.id, writeable.id);
    removeStagingMarker(cfg, record.id);
    if (record.kind === 'building') {
      // source === parent here: staged comps already carry the committed shape (`.type`, GeoPoint location).
      const comps = formatOmComps(writeable, writeable);
      const body = {...writeable, markerData: buildCommittedMarkerData(cfg, writeable), comps};
      addAggregatedFieldsToBody(body);
      getCollection(cfg.finalFilterCollection).addItems(body);
      getCollection('competitiveBuildingComps').addItems(...(comps ?? []));
      buildingBodies.push(body);
    } else {
      portfolioBody = {...writeable, markerData: buildCommittedMarkerData(cfg, writeable)};
      getCollection(cfg.finalFilterCollection).addItems(portfolioBody);
    }
  }

  if (portfolioBody) {
    getStore().dispatch(`infowindow/${bulkConfig.portfolio.finalInfoWindowAction}`, {
      subject: committedSubject(bulkConfig.portfolio, portfolioBody),
    });
    return true;
  }
  if (buildingBodies.length) {
    getStore().dispatch(`infowindow/${bulkConfig.building.finalInfoWindowAction}`, {
      subject: committedSubject(bulkConfig.building, buildingBodies[0]),
    });
    return true;
  }
  return false;
};

/**
 * A verdict that resolves the bundle commits every approved record in the same transaction, so a reload leaves nothing
 * written or all of it; statuses are re-read inside it, so two simultaneous last approvals commit once. A `merged`
 * verdict was already written by the merge. Resolves whether it opened a window: the next member's review, or a
 * committed record.
 */
const resolveBundleMember = async (item: StagedRecord, verdict: 'approve' | 'delete' | 'merged'): Promise<boolean> => {
  const whoAndNow = currentUserAction();
  const portfolio = bundlePortfolio(item);
  const memberIds = new Set(portfolio?.linkedDocuments.map((d) => d.id) ?? []);
  const ordered = portfolio
    ? [portfolio, ...(getStore().state.bulkCompetitiveBuildings as StagedRecord[])
        .filter((b) => memberIds.has(b.id))
        .sort(byStagedName)]
    : [item];
  const start = ordered.findIndex((r) => r.id === item.id);
  if (start === -1) throw new Error(`${item.name} isn't among its portfolio's members; reload the page and try again`);

  const outcome = await runInTransaction(async (transaction): Promise<BundleOutcome> => {
    const docs = await Promise.all(ordered.map((record) =>
      transaction.get(bulkConfig[record.kind].stagingCollection, record.id)));
    const fresh = new Map(ordered.map((record, i): [string, StagedRecord] => {
      const doc = docs[i];
      const current = record.id === item.id ? item : record;
      return [record.id, doc ? {...doc, id: record.id, kind: record.kind} as StagedRecord : current];
    }));
    const own = fresh.get(item.id)!;
    if (own.status === 'deleted' || (verdict === 'approve' && own.status === 'approved')) {
      return {kind: 'alreadyResolved', status: own.status};
    }

    const updates = createUpdateQueue();
    if (verdict !== 'merged') {
      own.status = verdict === 'approve' ? 'approved' : 'deleted';
      updates.queue(bulkConfig[item.kind].stagingCollection, item.id, verdict === 'approve'
        ? {status: 'approved', editedOn: whoAndNow}
        : {status: 'deleted', deleted: whoAndNow.timestamp, editedOn: whoAndNow});
    }

    const next = portfolio
      ? nextUnresolvedSibling(ordered, start, (r) => fresh.get(r.id)!.status !== 'review')
      : null;
    const approved = next ? [] : ordered.map((r) => fresh.get(r.id)!).filter((r) => r.status === 'approved');
    const committing = approved.filter((r) => !r[bulkConfig[r.kind].finalIdKey]);
    // A building already committed, or merged into an existing one, joins the portfolio through that record
    const existingIds = approved.flatMap((r) => (r.kind === 'building' ? r[bulkConfig.building.finalIdKey] ?? [] : []));

    const committedIds = new Map(committing.map((r) => [
      r.id, createDocRef(bulkConfig[r.kind].finalFirestoreCollection).id,
    ]));
    const linksTo = (kind: 'building' | 'portfolio') => committing
      .filter((r) => r.kind === kind)
      .map((r) => ({collection: bulkConfig[kind].finalFirestoreCollection, id: committedIds.get(r.id)!}));
    const existingLinks = existingIds.map((id) => ({collection: bulkConfig.building.finalFirestoreCollection, id}));
    const committed = committing.map((record): CommittedRecord => {
      const cfg = bulkConfig[record.kind];
      const siblingLinks = record.kind === 'building' ? linksTo('portfolio') : [...existingLinks, ...linksTo('building')];
      const writeable = buildCommittedBody(record, committedIds.get(record.id)!, siblingLinks, whoAndNow);
      return {record, cfg, writeable};
    });
    const releases = await planSurvivorReleases(transaction, existingIds, linksTo('portfolio'));
    for (const {record, cfg, writeable} of committed) {
      transaction.set(cfg.finalFirestoreCollection, writeable.id, writeable);
      updates.queue(cfg.stagingCollection, record.id, {
        status: 'approved',
        [cfg.finalIdKey]: writeable.id,
        editedOn: whoAndNow,
      });
    }
    for (const {id, update} of releases) {
      updates.queue(bulkConfig.building.finalFirestoreCollection, id, {...update, editedOn: whoAndNow});
    }
    updates.flush(transaction);
    return next ? {kind: 'nextForReview', next: fresh.get(next.id)!} : {kind: 'resolved', committed, releases};
  });

  if (outcome.kind === 'alreadyResolved') {
    getStore().commit('setSnack', {message: `Already ${outcome.status}.`});
    return false;
  }
  if (verdict === 'approve') {
    getStore().dispatch('infowindow/closeInfoWindow');
  } else {
    getStore().dispatch('infowindow/closeInfoWindowTab', item.id);
  }
  removeStagingMarker(bulkConfig[item.kind], item.id);
  if (outcome.kind === 'nextForReview') {
    await placeBulkMarker(outcome.next);
    return true;
  }
  const opened = showCommittedRecords(outcome.committed);
  refreshBackendFiltersForDocs(showReleasedSurvivors(outcome.releases, whoAndNow));
  return opened;
};

/** Moves a bundle on once a building was merged into an existing record, as approving it would. */
const advanceBulkBundle = (item: StagedRecord): Promise<boolean> => resolveBundleMember(item, 'merged');

/** Approves a staged record and advances; the bundle commits once every record is resolved. */
async function approveBulkUnit(stagedItem: StagedRecord): Promise<void> {
  try {
    await flushOpenReviewEdits(stagedItem.id);
    await resolveBundleMember(stagedItem, 'approve');
  } catch (error) {
    throw wrapError(error, 'Failed to approve bulk upload record');
  }
}

/** Deleting a portfolio leaves its buildings linked and reviewable. */
async function deleteBulkItem(item: StagedRecord): Promise<void> {
  try {
    await resolveBundleMember(item, 'delete');
  } catch (error) {
    throw wrapError(error, 'Failed to delete bulk upload record');
  }
}

export {advanceBulkBundle, approveBulkUnit, deleteBulkItem};
